import { acceptHMRUpdate, defineStore } from 'pinia';
import { leadingTrailingDebounce } from '~/composables/useDebounce';
import { useLogger } from '~/composables/useLogger';
import { DEFAULT_FETCH_OPTIONS } from '~/utils/defaultFetchOptions';
import type {
  OnboardingAnswers,
  OnboardingData,
  OnboardingInput
} from '~/types/onboarding';
import {
  DEFAULT_ONBOARDING_ANSWERS,
  ONBOARDING_STATUS_IN_PROGRESS
} from '~/types/onboarding';
import {
  mapAnswersToInput,
  mapDataToAnswers,
  mergeOnboardingInputIntoAnswers
} from '~/utils/onboarding/onboardingMappers';
import { resolvePortalOnboardingStatus } from '~/utils/onboarding/isOnboardingPreferencesComplete';
import { identifyCustomerPreferences } from '~/utils/ninetailed/identifyCustomerPreferences';
import { pickNinetailedIdentifyTraits } from '~/utils/ninetailed/pickNinetailedIdentifyTraits';
import { findAdditionalConfigurationIdByType } from '~/utils/contentful/findAdditionalConfigurationByType';
import { ONBOARDING_SETTINGS_TYPE } from '~/utils/onboardingSettings/configurationSchema';
import { mapAnswersToPardotPayload } from '~/utils/onboardingSettings/mapAnswersToPardotPayload';

const PREFERENCES_API_PATH = '/api/magento/customer/preferences';
const FORMHANDLER_API_PATH = '/api/formhandler';
const SYNC_DEBOUNCE_MS = 2000;
const MAX_SYNC_RETRIES = 3;

/**
 * Customer onboarding preferences — local state is authoritative for the UI.
 * Magento persistence runs in the background (compare-list pattern).
 */
export const useCustomerPreferencesStore = defineStore(
  'customer-preferences',
  () => {
    const { $shopStore, $locale, $ninetailed, $globalPageSettings } =
      useNuxtApp();
    const { loggedIn: isAuthenticated, user } = useOidcAuth();
    const logger = useLogger();

    const answers = ref<OnboardingAnswers>({ ...DEFAULT_ONBOARDING_ANSWERS });
    const onboardingData = ref<OnboardingData | null>(null);
    const hasLoaded = ref(false);
    const isLoading = ref(false);
    const loadError = ref<Error | null>(null);
    const syncing = ref(false);
    const syncError = ref<Error | null>(null);
    const retryCount = ref(0);
    const syncStatusOverride = ref<
      OnboardingInput['cp_onboarding_status'] | undefined
    >(undefined);
    const includeStatusOnSync = ref(false);
    const pendingSync = ref(false);
    const hasPendingSync = ref(false);
    let stateGeneration = 0;
    let fetchRequestId = 0;
    /**
     * Dedup key claimed for in-flight / last successful Pardot POST.
     * Set optimistically before fetch to collapse concurrent duplicate submits;
     * cleared on failure so an independent retry can run.
     */
    let lastPardotPayloadKey: string | null = null;
    let pardotRetryCount = 0;
    let pardotRetryTimer: ReturnType<typeof setTimeout> | null = null;

    function clearPardotRetryTimer(): void {
      if (pardotRetryTimer !== null) {
        clearTimeout(pardotRetryTimer);
        pardotRetryTimer = null;
      }
    }

    function applyInputToLocalState(
      input: OnboardingInput,
      status?: OnboardingInput['cp_onboarding_status']
    ): void {
      answers.value = mergeOnboardingInputIntoAnswers(answers.value, input);
      onboardingData.value = {
        ...(onboardingData.value ?? {}),
        ...mapAnswersToInput(answers.value),
        ...(status !== undefined ? { cp_onboarding_status: status } : {})
      };
    }

    function syncPreferencesIdentify(): void {
      const oid = user?.value?.claims?.oid?.toString();
      if (!oid) {
        return;
      }

      const claims = user.value?.claims as Record<string, unknown> | undefined;
      const entraTraits = claims ? pickNinetailedIdentifyTraits(claims) : {};

      const ninetailed = $ninetailed.value;
      identifyCustomerPreferences({
        identify: ninetailed?.identify?.bind(ninetailed) as
          | ((
              userId: string,
              traits?: Record<string, unknown>
            ) => Promise<unknown>)
          | undefined,
        userId: oid,
        answers: answers.value,
        onboardingData: onboardingData.value,
        baseTraits: {
          loginStatus: 'loggedIn',
          user_id: oid,
          ...entraTraits
        },
        onError: (error: Error) => {
          logger.error(error);
        }
      });
    }

    async function fetchPreferences(): Promise<OnboardingData | null> {
      if (!isAuthenticated.value) {
        hasLoaded.value = true;
        return null;
      }

      const requestId = ++fetchRequestId;
      const requestGeneration = stateGeneration;
      isLoading.value = true;
      loadError.value = null;

      try {
        const response = await $fetch<OnboardingData | null>(
          PREFERENCES_API_PATH,
          {
            ...DEFAULT_FETCH_OPTIONS,
            method: 'GET',
            query: {
              store: $shopStore.value,
              locale: $locale.value
            }
          }
        );

        if (
          requestId !== fetchRequestId ||
          requestGeneration !== stateGeneration ||
          !isAuthenticated.value
        ) {
          return null;
        }

        onboardingData.value = response;
        answers.value = mapDataToAnswers(response);
        syncStatusOverride.value = undefined;
        includeStatusOnSync.value = false;
        retryCount.value = 0;
        syncError.value = null;
        syncPreferencesIdentify();
        return response;
      } catch (error) {
        if (
          requestId !== fetchRequestId ||
          requestGeneration !== stateGeneration ||
          !isAuthenticated.value
        ) {
          return null;
        }

        loadError.value =
          error instanceof Error
            ? error
            : new Error('Failed to load customer preferences');
        logger.error(loadError.value);
        return null;
      } finally {
        if (
          requestId === fetchRequestId &&
          requestGeneration === stateGeneration
        ) {
          isLoading.value = false;
          hasLoaded.value = true;
        }
      }
    }

    function applyInput(
      input: OnboardingInput,
      status?: OnboardingInput['cp_onboarding_status']
    ): void {
      applyInputToLocalState(input, status);

      if (status !== undefined) {
        syncStatusOverride.value = status;
        includeStatusOnSync.value = true;
      }

      hasPendingSync.value = true;
      syncPreferencesIdentify();
      debouncedSync();
    }

    function applyAnswers(nextAnswers: OnboardingAnswers): void {
      const status = resolvePortalOnboardingStatus(
        nextAnswers,
        onboardingData.value?.cp_onboarding_status
      );

      answers.value = { ...nextAnswers };
      onboardingData.value = {
        ...(onboardingData.value ?? {}),
        ...mapAnswersToInput(nextAnswers),
        ...(status !== undefined ? { cp_onboarding_status: status } : {})
      };

      if (status !== undefined) {
        syncStatusOverride.value = status;
        includeStatusOnSync.value = true;
      } else {
        syncStatusOverride.value = undefined;
        includeStatusOnSync.value = false;
      }

      hasPendingSync.value = true;
      syncPreferencesIdentify();
      debouncedSync();
    }

    function reset(): void {
      stateGeneration += 1;
      fetchRequestId += 1;
      onboardingData.value = null;
      answers.value = { ...DEFAULT_ONBOARDING_ANSWERS };
      hasLoaded.value = false;
      isLoading.value = false;
      loadError.value = null;
      syncError.value = null;
      syncStatusOverride.value = undefined;
      includeStatusOnSync.value = false;
      pendingSync.value = false;
      hasPendingSync.value = false;
      retryCount.value = 0;
      clearPardotRetryTimer();
      lastPardotPayloadKey = null;
      pardotRetryCount = 0;
    }

    function buildSyncBody(): OnboardingInput {
      if (
        !includeStatusOnSync.value &&
        syncStatusOverride.value === undefined
      ) {
        return mapAnswersToInput(answers.value);
      }

      const status =
        syncStatusOverride.value ??
        onboardingData.value?.cp_onboarding_status ??
        ONBOARDING_STATUS_IN_PROGRESS;

      return mapAnswersToInput(answers.value, status);
    }

    function handleSyncError(error: unknown): void {
      const message =
        error instanceof Error ? error.message : 'Failed to sync preferences';
      syncError.value = error instanceof Error ? error : new Error(message);
      logger.error(syncError.value);

      if (retryCount.value < MAX_SYNC_RETRIES) {
        retryCount.value += 1;
        debouncedSync();
        return;
      }

      logger.error(
        new Error(
          `Customer preferences sync failed after ${MAX_SYNC_RETRIES} attempts`
        )
      );
    }

    /**
     * Fire-and-forget Pardot sync via `/api/formhandler`. Never blocks Magento
     * persistence — Magento remains the source of truth.
     * Optimistic dedupe collapses concurrent identical POSTs; failures clear the
     * claim and schedule a bounded independent retry (Magento success alone must
     * not drop the Pardot sync).
     */
    async function syncToPardot(): Promise<void> {
      if (!isAuthenticated.value) {
        return;
      }

      const entryId = findAdditionalConfigurationIdByType(
        $globalPageSettings?.value?.additionalConfigurationCollection,
        ONBOARDING_SETTINGS_TYPE
      );

      if (!entryId) {
        return;
      }

      const email =
        typeof user.value?.userInfo?.email === 'string'
          ? user.value.userInfo.email
          : undefined;

      if (!email) {
        logger.error(
          new Error('Onboarding Pardot sync skipped: missing user email')
        );
        return;
      }

      const pardotPayload = mapAnswersToPardotPayload(answers.value, email);
      const payloadKey = JSON.stringify({
        ...pardotPayload,
        applications: pardotPayload.applications
          ? [...pardotPayload.applications].sort()
          : undefined
      });

      if (payloadKey === lastPardotPayloadKey) {
        return;
      }

      // Claim before await so a queued Magento sync cannot double-POST.
      lastPardotPayloadKey = payloadKey;

      try {
        await $fetch(FORMHANDLER_API_PATH, {
          ...DEFAULT_FETCH_OPTIONS,
          method: 'POST',
          body: {
            internal_locale: $locale.value,
            internal_entryID: entryId,
            ...pardotPayload
          }
        });
        pardotRetryCount = 0;
      } catch (error) {
        if (lastPardotPayloadKey === payloadKey) {
          lastPardotPayloadKey = null;
        }

        logger.error(
          error instanceof Error
            ? error
            : new Error('Onboarding Pardot formhandler submit failed')
        );

        if (isAuthenticated.value && pardotRetryCount < MAX_SYNC_RETRIES) {
          pardotRetryCount += 1;
          clearPardotRetryTimer();
          pardotRetryTimer = setTimeout(() => {
            pardotRetryTimer = null;
            void syncToPardot();
          }, SYNC_DEBOUNCE_MS);
        }
      }
    }

    async function syncToMagento(): Promise<void> {
      if (!isAuthenticated.value || !hasPendingSync.value) {
        return;
      }

      if (syncing.value) {
        pendingSync.value = true;
        return;
      }

      // Fresh Magento-triggered attempt — reset Pardot retry budget.
      pardotRetryCount = 0;
      clearPardotRetryTimer();
      // Same trigger as Magento; failures must not affect Magento sync.
      void syncToPardot();

      const requestGeneration = stateGeneration;
      hasPendingSync.value = false;
      syncing.value = true;
      syncError.value = null;

      try {
        const response = await $fetch<OnboardingData | null>(
          PREFERENCES_API_PATH,
          {
            ...DEFAULT_FETCH_OPTIONS,
            method: 'PUT',
            query: {
              store: $shopStore.value,
              locale: $locale.value
            },
            body: buildSyncBody()
          }
        );

        if (requestGeneration !== stateGeneration || !isAuthenticated.value) {
          return;
        }

        onboardingData.value = {
          ...(onboardingData.value ?? {}),
          ...(response ?? {})
        } as OnboardingData;
        // Local answers stay authoritative — Magento responses can be stale while
        // another sync is in flight (compare-list pattern).
        // Keep status override when a follow-up was queued during this request
        // (e.g. complete() while an in_progress PUT is still running).
        if (!pendingSync.value) {
          syncStatusOverride.value = undefined;
          includeStatusOnSync.value = false;
        }
        retryCount.value = 0;
      } catch (error) {
        if (requestGeneration === stateGeneration && isAuthenticated.value) {
          hasPendingSync.value = true;
          handleSyncError(error);
        }
      } finally {
        syncing.value = false;

        if (pendingSync.value) {
          pendingSync.value = false;
          void syncToMagento();
        }
      }
    }

    const debouncedSync = leadingTrailingDebounce(() => {
      void syncToMagento();
    }, SYNC_DEBOUNCE_MS);

    return {
      answers,
      onboardingData,
      hasLoaded,
      isLoading,
      loadError,
      syncing,
      syncError,
      fetchPreferences,
      applyInput,
      applyAnswers,
      flushSync: syncToMagento,
      reset
    };
  }
);

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useCustomerPreferencesStore, import.meta.hot)
  );
}
